import { atom } from 'nanostores';
import type { BillingInterval } from '~/lib/api/billing';

/**
 * Drives the plan-activation modal through the async gap between a payment and the
 * webhook that actually grants the plan:
 *   - `confirming` — payment done, waiting (~3–5s) for the webhook to activate the plan.
 *   - `active`     — plan is live; show the celebration and the real numbers.
 * `null` when there's nothing to show. In-place upgrades skip straight to `active`
 * (the plan is already paid), so they never show the confirming state.
 */
export type PlanActivation =
  | { status: 'confirming' }
  | { status: 'active'; tier: string; name: string; credits: number; interval: BillingInterval };

export const $planWelcome = atom<PlanActivation | null>(null);

// Plus/Pro/Max are the plans sold today; the rest are grandfathered website plans.
const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
};

/** Enter the "confirming your payment" state while the webhook is pending. */
export function beginPlanActivation(): void {
  $planWelcome.set({ status: 'confirming' });
}

/** The plan is live — celebrate with the real numbers. No-op for the free tier. */
export function celebratePlan(tier: string, credits: number, interval: BillingInterval = 'month'): void {
  if (tier === 'free') {
    return;
  }

  $planWelcome.set({ status: 'active', tier, name: PLAN_NAMES[tier] ?? tier, credits, interval });
}

export function dismissPlanWelcome(): void {
  $planWelcome.set(null);
}
